/**
 * The workspace on a narrow screen (a phone, below `COMPACT_WIDTH`): one open window fills it,
 * and a bar at the bottom switches between the open windows (tiled and floating alike). The
 * layout itself is not changed, so a wider screen shows the same tiles again; a window opened
 * from a menu or a shortcut is shown at once.
 */
import { For, Show, createEffect, createMemo, createSignal, on, type JSX } from "solid-js";
import { useApp } from "../app/context";
import * as L from "./layout";
import { WindowBody } from "./WindowBody";

/** One window at a time, with the bar to switch. */
export function CompactView(): JSX.Element {
  const { wm } = useApp();
  const open = createMemo(() => L.openWindows(wm.layout()), undefined, { equals: (a, b) => a.length === b.length && a.every((w, i) => w === b[i]) });
  const [shown, setShown] = createSignal<string | null>(L.compactShown(null, [], open()));
  /** The open windows when last looked at (to see which one just opened). */
  let before = open();
  createEffect(
    on(open, (after) => {
      setShown((current) => L.compactShown(current, before, after));
      before = after;
    }),
  );

  return (
    <div class="compact" data-compact>
      <div class="compact-body">
        <Show when={shown()} keyed fallback={<div class="empty-tiles">No window is open. Open one from the Windows menu.</div>}>
          {(win) => <WindowBody window={win} />}
        </Show>
      </div>
      <nav class="compact-bar" aria-label="Open windows">
        <For each={open()}>
          {(win) => (
            <div class="compact-tab" classList={{ active: win === shown() }} data-compact-tab={win}>
              <button type="button" class="tab-label" onClick={() => setShown(win)}>
                {wm.def(win)?.title ?? win}
              </button>
              <Show when={win === shown()}>
                <button type="button" class="tab-x" title="Close window" aria-label={`Close ${wm.def(win)?.title ?? win}`} onClick={() => wm.close(win)}>
                  ×
                </button>
              </Show>
            </div>
          )}
        </For>
      </nav>
    </div>
  );
}
